/**
 * useHideReveal - the GM's two verbs for hidden figures (plan map-display, G1).
 *
 * HIDE (a public figure; one born hidden comes from the spawn dialog, useHiddenFigures `reserve`):
 *   1. its record is written to `gmHidden` - FIRST, and awaited: if it cannot
 *      be stored, nothing else happens and the GM is told (a figure taken off
 *      the shared world with nowhere to live would be lost);
 *   2. this screen adopts it: it leaves the shared slice, so the roster push
 *      deletes its shared combatant and its shared token (useShellRosterPush);
 *   3. its seat in the order is taken away, its initiative kept in the record;
 *   4. Undo is cleared - players had seen it, and an undo must not bring it
 *      back into view (ADR 2026-10-07).
 *
 * REVEAL:
 *   1. its record becomes a tombstone - FIRST, and awaited: refused, nothing
 *      changes and the GM is told (its alias stays taken either way);
 *   2. this screen forgets it - it joins the shared slice and the push sends
 *      it - and its token, no longer hidden, is routed to the shared map;
 *   3. it takes its place in the order with the initiative it was hidden with;
 *   4. a public line says it emerges.
 *
 * Both verbs await the server, so what they change after it is read from the
 * scope as it is THEN (useMirror): a turn ended during the round trip must
 * not be undone by a stale write.
 *
 * @param {object} s - hidden (useHiddenFigures), roster, tokens, writeToken (routed), turnTracker,
 *   resetHistory (both verbs clear Undo), contacts (a reveal forgets its level), addNarrative, pushToast, connectedPlayers and user (a played character is never hidden)
 * @returns {{ hide: (id: string) => Promise<boolean>, reveal: (id: string) => Promise<boolean> }}
 */
import { useCallback } from 'react';
import { hideFigure } from '../engine/roster/hidden';
import { emergeBeat } from '../engine/attackBeat';
import { actingByFrom } from '../engine/roster/actingBy';
import { toMs } from '../utils/timestamps';
import useMirror from './useMirror';

/** The initiative a participant holds, to keep while it is out of the order. */
function keptInitiative(participant) {
  if (!participant) return null;
  const { initiative: value, initiativeRoll: roll, initiativeBonus: bonus } = participant;
  return { value, roll, bonus };
}

/** Store a figure's record (the one writer, useHiddenFigures); false, and the GM told, when it is refused. */
async function stored({ hidden, pushToast }, fig) {
  try {
    await hidden.saveFigure(fig);
    return true;
  } catch {
    pushToast?.(`${fig.combatant.name} could not be hidden - the server refused it.`, {
      type: 'error',
      duration: 8000,
    });
    return false;
  }
}

/**
 * Is a PLAYER playing this combatant? Such a one is never hidden (ruling
 * 2026-10-07): the player's own writes would bring it back on every screen,
 * and a character who sneaks is Stealth's to show, not a hidden figure. The
 * GM's own seat is not a player's - the GM acts as the very NPCs it hides.
 */
function playedByAPlayer({ connectedPlayers, user }, id) {
  const others = (connectedPlayers ?? []).filter((p) => p.uid !== user?.uid);
  return Object.values(actingByFrom(others, toMs)).includes(id);
}

/** The record for this figure, its alias claimed at once (two hides in one round trip must not share one). */
function recordFor(s, id) {
  const { hidden, roster, tokens, turnTracker } = s;
  const combatant = roster.find((c) => c.id === id);
  if (!combatant || hidden.isHidden(id)) return null;
  const seat = turnTracker.trackerState.participants.find((p) => p.combatantId === id);
  const fig = hideFigure(combatant, {
    token: tokens[id] ?? null,
    initiative: keptInitiative(seat),
    taken: hidden.takenAliases(),
    bornHidden: false,
  });
  hidden.claimAlias(fig.alias);
  return fig;
}

function useHide(s) {
  const latest = useMirror(s);
  return useCallback(
    async (id) => {
      if (playedByAPlayer(latest.current, id)) return refusePlayed(latest.current, id);
      const fig = recordFor(latest.current, id);
      if (!fig || !(await stored(latest.current, fig))) return false;
      const { hidden, turnTracker, resetHistory } = latest.current;
      hidden.adopt(fig);
      const seat = turnTracker.trackerState.participants.find((p) => p.combatantId === id);
      if (seat) turnTracker.removeParticipant(seat.id);
      resetHistory();
      return true;
    },
    [latest],
  );
}

/** Tell the GM why a played character stays visible; false, as a refused hide. */
function refusePlayed({ roster, pushToast }, id) {
  const name = roster.find((c) => c.id === id)?.name ?? 'That character';
  pushToast?.(`${name} is played by a player and cannot be hidden.`, { type: 'error', duration: 6000 });
  return false;
}

/** Tell the GM a reveal did not happen: the figure is still hidden. */
function refused(s, combatant) {
  s.pushToast?.(`${combatant.name} could not be revealed - the server refused it. Still hidden.`, {
    type: 'error',
    duration: 8000,
  });
}

/**
 * A revealed figure comes out: its token onto the shared map (the routed writer: it is no longer hidden), its seat
 * in a running order with its kept initiative, the emerge beat in its column (every screen plays the arrival), Undo
 * cleared as a hide clears it (an Undo across the reveal would put back a roster the hidden store has left), and
 * what the party knew of it forgotten - hidden again, it starts unknown (useStealthContacts).
 */
function emerge(s, id, fig, combatant) {
  if (fig.token) s.writeToken(fig.token, { immediate: true });
  if (s.turnTracker.trackerState.active) {
    s.turnTracker.addTabParticipant(id, { name: combatant.name, sheet: combatant.sheet, initiative: fig.initiative });
  }
  const at = Date.now();
  const text = `👁 ${combatant.name} emerges.`;
  s.addNarrative(id, { type: 'info', text, targetName: combatant.name, beat: emergeBeat(id, at), timestamp: at });
  s.resetHistory();
  s.contacts?.forget?.(id);
}

function useReveal(s) {
  const latest = useMirror(s);
  return useCallback(
    async (id) => {
      const fig = latest.current.hidden.figures.get(id);
      const combatant = latest.current.roster.find((c) => c.id === id) ?? fig?.combatant;
      if (!fig || !combatant) return false;
      try {
        // The tombstone first: refused, nothing has changed and the figure is still hidden.
        await latest.current.hidden.endStored(id);
      } catch {
        refused(latest.current, combatant);
        return false;
      }
      emerge(latest.current, id, fig, combatant);
      return true;
    },
    [latest],
  );
}

export default function useHideReveal(s) {
  return { hide: useHide(s), reveal: useReveal(s) };
}
